import { useCallback } from "react";
import { useMutation } from "@apollo/client/react";
import { calculateSortOrder } from "@salestrak/ui";
import {
  BulkDropIssuesToStageDocument,
  CurrentUserIssueCountsDocument,
  IssueListGroupCountsDocument,
  type IssueRow,
  MoveIssueToStageDocument,
  ReorderIssueDocument,
} from "#app/lib/data";
import {
  removeIssueFromStageCache,
  insertIssueIntoStageCache,
  reorderIssueInStageCache,
  dropIssuesIntoStageCache,
  shiftStageGroupCounts,
} from "./kanban-cache-utils";
import type { Omittable } from "@salestrak/ui";
import { optimisticIssue } from "#app/lib/data/optimistic-issue";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type MoveIssueToStageParams = Omittable<{
  issue: IssueRow;
  fromStageId: string;
  toStageId: string;
  newSortOrder: number;
  upperNeighborKey?: string;
}>;

type ReorderIssueParams = Omittable<{
  issue: IssueRow;
  stageId: string;
  upperNeighborKey?: string;
  lowerNeighborKey?: string;
  newSortOrder: number;
}>;

type DropIssuesParams = Omittable<{
  /** The dragged rows, ordered as they should land, top → bottom. */
  rows: IssueRow[];
  toStageId: string;
  /** Upper anchor of the drop slot (the block chains after it in cache). */
  upperNeighborKey?: string;
  /** Sort bounds of the drop slot — the block spreads its orders between them. */
  upperSortValue?: number;
  lowerSortValue?: number;
  /**
   * The drop landed past a tail the column has not loaded (a collapsed stage,
   * or one whose last page never scrolled in): no bounds to spread into, so
   * the block lands at the end — the list's MAX_SAFE_INTEGER grammar, stepped
   * back so every row keeps a distinct order.
   */
  atUnloadedTail?: boolean;
}>;

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Per-bucket counts (`issueGroupCounts`) are a separate root field the stage
 * list cache writes never touch. Each mutation that changes a stage's
 * membership shifts the cached stage counts in its optimistic update, so the
 * board's auto-collapse reacts in the same frame as the drop, and refetches
 * the active count queries once the server has answered so every argument
 * set (the Only-Me chip included) settles on the server's figure.
 */
const COUNT_REFETCH = {
  refetchQueries: [IssueListGroupCountsDocument, CurrentUserIssueCountsDocument],
};

export function useKanbanMutations() {
  const [moveIssueMutation] = useMutation(MoveIssueToStageDocument);
  const [reorderIssueMutation] = useMutation(ReorderIssueDocument);
  const [bulkDropMutation] = useMutation(BulkDropIssuesToStageDocument);

  const moveIssueToStage = useCallback(
    async ({
      issue,
      fromStageId,
      toStageId,
      newSortOrder,
      upperNeighborKey,
    }: MoveIssueToStageParams) => {
      await moveIssueMutation({
        ...COUNT_REFETCH,
        variables: {
          issueId: issue.issueId,
          stageId: toStageId,
          sortOrder: newSortOrder,
        },
        optimisticResponse: {
          moveIssueToStage: optimisticIssue({
            ...issue,
            sortOrder: newSortOrder,
            stageId: toStageId,
            assignees: issue.assignees ?? [],
          }),
        },
        update(cache) {
          removeIssueFromStageCache(cache, fromStageId, issue.issueId);
          insertIssueIntoStageCache(
            cache,
            toStageId,
            { issueId: issue.issueId, sortOrder: newSortOrder },
            upperNeighborKey,
          );
          shiftStageGroupCounts(cache, [{ fromStageId, toStageId }]);
        },
      });
    },
    [moveIssueMutation],
  );

  const reorderIssue = useCallback(
    async ({
      issue,
      stageId,
      upperNeighborKey,
      lowerNeighborKey,
      newSortOrder,
    }: ReorderIssueParams) => {
      await reorderIssueMutation({
        variables: {
          issueId: issue.issueId,
          afterIssueId: upperNeighborKey ?? null,
          beforeIssueId: lowerNeighborKey ?? null,
        },
        optimisticResponse: {
          updateIssueSortOrder: {
            __typename: "Issue",
            id: issue.id,
            issueId: issue.issueId,
            sortOrder: newSortOrder,
          },
        },
        update(cache) {
          reorderIssueInStageCache(
            cache,
            stageId,
            issue.issueId,
            upperNeighborKey,
            lowerNeighborKey,
          );
        },
      });
    },
    [reorderIssueMutation],
  );

  /**
   * Lands a multi-card drop as ONE bulk mutation with ONE optimistic cache
   * write, so the whole selection appears in the target column on the same
   * frame — a per-row loop trickled the cards in as each call resolved. Sort
   * orders are spread client-side between the drop-slot bounds and sent as
   * explicit placements; the server writes them verbatim, so a row from any
   * source stage lands exactly where the block sits. Returns the rows the
   * server actually moved (its per-item edit filter may drop some).
   */
  const dropIssues = useCallback(
    async ({
      rows,
      toStageId,
      upperNeighborKey,
      upperSortValue,
      lowerSortValue,
      atUnloadedTail = false,
    }: DropIssuesParams) => {
      const groupIds = new Set(rows.map((r) => r.issueId));
      // A drop boundary that is itself part of the dragged group vacates as
      // the group lands, so it cannot anchor the block — fall back to sorted
      // insertion. The sort VALUES stay: the vacating card's old sort still
      // bounds the interval the block spreads into.
      const afterIssueId =
        upperNeighborKey != null && !groupIds.has(upperNeighborKey)
          ? upperNeighborKey
          : undefined;
      let prevSort = upperSortValue;
      const placed = rows.map((issue, i) => {
        const sortOrder = atUnloadedTail
          ? Number.MAX_SAFE_INTEGER - 1000 * (rows.length - 1 - i)
          : calculateSortOrder(prevSort, lowerSortValue);
        prevSort = sortOrder;
        return { issue, sortOrder };
      });
      const result = await bulkDropMutation({
        ...COUNT_REFETCH,
        variables: {
          stageId: toStageId,
          placements: placed.map((p) => ({
            issueId: p.issue.issueId,
            sortOrder: p.sortOrder,
          })),
        },
        optimisticResponse: {
          bulkMoveIssuesToStage: placed.map((p) =>
            optimisticIssue({
              ...p.issue,
              stageId: toStageId,
              sortOrder: p.sortOrder,
              assignees: p.issue.assignees ?? [],
            }),
          ),
        },
        update(cache, { data }) {
          // The server filters placements through its per-item edit check and
          // may move only a subset — land exactly the rows the result carries
          // (optimistic pass: all of them; final pass: the survivors), so a
          // refused row never shows a phantom move.
          const moved = new Set(
            (data?.bulkMoveIssuesToStage ?? []).map((r) => r.issueId),
          );
          dropIssuesIntoStageCache(cache, {
            orderedRows: placed
              .filter((p) => moved.has(p.issue.issueId))
              .map((p) => ({
                issueId: p.issue.issueId,
                sortOrder: p.sortOrder,
                fromStageId: p.issue.stageId,
              })),
            toStageId,
            afterIssueId,
          });
          shiftStageGroupCounts(
            cache,
            placed
              .filter((p) => moved.has(p.issue.issueId))
              .map((p) => ({ fromStageId: p.issue.stageId, toStageId })),
          );
        },
      });
      return result.data?.bulkMoveIssuesToStage ?? [];
    },
    [bulkDropMutation],
  );

  return { moveIssueToStage, reorderIssue, dropIssues };
}
